/**
 * SEO component that queries for data with
 * Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { useStaticQuery, graphql } from 'gatsby'
import { getSrc } from 'gatsby-plugin-image'

function getCapitalized(str) {
  if (!str) return '';
  var strList = str.split(" ")
  var updatedStr = ""
  strList.forEach(el => {
    updatedStr += " "
    updatedStr += el.slice(0, 1).toUpperCase() + el.slice(1)
  })

  return updatedStr.trim()
}

const SEO = ({ 
  title, 
  description, 
  image, 
  article,
  song,
  album,
  artist, 
  pathname,
  schema,
  keywords,
  publishedAt,
  modifiedAt,
  songData,
  albumData,
  artistData,
  children
}) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            defaultTitle: title
            titleTemplate
            defaultDescription: description
            siteUrl
            defaultImage
            twitterUsername
            facebookAppId
          }
        }
      }
    `
  )

  const {
    defaultTitle,
    titleTemplate,
    defaultDescription,
    siteUrl,
    defaultImage,
    twitterUsername,
    facebookAppId
  } = site.siteMetadata

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: image ? `${siteUrl}${image}` : `${siteUrl}${defaultImage}`,
    url: `${siteUrl}${pathname || '/'}`,
    keywords: keywords || ['tamil songs', 'tamil lyrics', 'tamil music', 'thamizh songs', 'thamizh lyrics'],
    publishedAt: publishedAt || new Date().toISOString(),
    modifiedAt: modifiedAt || new Date().toISOString()
  }

  // Schema.org markup
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "url": siteUrl,
    "name": defaultTitle,
    "description": defaultDescription,
    "image": `${siteUrl}${defaultImage}`,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${siteUrl}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  }

  // Song schema
  const songSchema = songData ? {
    "@context": "https://schema.org",
    "@type": "MusicComposition",
    "name": songData.title,
    "composer": {
      "@type": "Person",
      "name": songData.musicDirector
    },
    "lyricist": {
      "@type": "Person",
      "name": songData.lyricist
    },
    "inAlbum": {
      "@type": "MusicAlbum",
      "name": songData.album
    },
    "datePublished": songData.releaseDate,
    "musicCompositionForm": "Song",
    "musicalKey": songData.musicalKey || "",
    "recordedAs": {
      "@type": "MusicRecording",
      "byArtist": {
        "@type": "MusicGroup",
        "name": songData.singers
      }
    }
  } : null;

  // Album schema
  const albumSchema = albumData ? {
    "@context": "https://schema.org",
    "@type": "MusicAlbum",
    "name": albumData.title,
    "byArtist": {
      "@type": "MusicGroup",
      "name": albumData.artist
    },
    "numTracks": albumData.trackCount,
    "datePublished": albumData.releaseDate
  } : null;

  // Artist schema
  const artistSchema = artistData ? {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": artistData.name,
    "jobTitle": artistData.role || "Singer",
    "description": artistData.description,
    "image": artistData.image ? `${siteUrl}${artistData.image}` : null
  } : null;

  // Article schema
  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": seo.title,
    "image": [seo.image],
    "datePublished": seo.publishedAt,
    "dateModified": seo.modifiedAt,
    "author": {
      "@type": "Person",
      "name": "Thamizh Songs Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": defaultTitle,
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}${defaultImage}`
      }
    },
    "description": seo.description,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": seo.url
    }
  } : null;

  // BreadcrumbList schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": siteUrl
      }
    ]
  };

  // Add second breadcrumb item if we have a content type
  if (song) {
    breadcrumbSchema.itemListElement.push({
      "@type": "ListItem",
      "position": 2,
      "name": "Songs",
      "item": `${siteUrl}/songs`
    });
    breadcrumbSchema.itemListElement.push({
      "@type": "ListItem",
      "position": 3,
      "name": title,
      "item": seo.url
    });
  } else if (album) {
    breadcrumbSchema.itemListElement.push({
      "@type": "ListItem",
      "position": 2,
      "name": "Albums",
      "item": `${siteUrl}/albums`
    });
    breadcrumbSchema.itemListElement.push({
      "@type": "ListItem",
      "position": 3,
      "name": title,
      "item": seo.url
    });
  } else if (artist) {
    breadcrumbSchema.itemListElement.push({
      "@type": "ListItem",
      "position": 2,
      "name": "Artists",
      "item": `${siteUrl}/artists`
    });
    breadcrumbSchema.itemListElement.push({
      "@type": "ListItem",
      "position": 3,
      "name": title,
      "item": seo.url
    });
  } else if (pathname && pathname !== '/') {
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.length > 0) {
      breadcrumbSchema.itemListElement.push({
        "@type": "ListItem",
        "position": 2,
        "name": getCapitalized(pathSegments[0]),
        "item": `${siteUrl}/${pathSegments[0]}`
      });
    }
  }

  // Choose the appropriate schema based on content type
  let contentSchema = null;
  if (songSchema) contentSchema = songSchema;
  else if (albumSchema) contentSchema = albumSchema;
  else if (artistSchema) contentSchema = artistSchema;
  else if (articleSchema) contentSchema = articleSchema;

  // Merge with custom schema if provided
  const mergedSchema = schema ? { ...defaultSchema, ...schema } : defaultSchema;

  // Create an array of all schemas to include
  const schemas = [mergedSchema];
  if (contentSchema) schemas.push(contentSchema);
  schemas.push(breadcrumbSchema);

  return (
    <Helmet
      title={seo.title}
      titleTemplate={titleTemplate}
      htmlAttributes={{
        lang: 'ta',
      }}
      meta={[
        // Basic meta tags
        {
          name: 'description',
          content: seo.description,
        },
        {
          name: 'keywords',
          content: Array.isArray(seo.keywords) ? seo.keywords.join(', ') : seo.keywords,
        },
        // Open Graph / Facebook
        {
          property: 'og:title',
          content: seo.title,
        },
        {
          property: 'og:description',
          content: seo.description,
        },
        {
          property: 'og:image',
          content: seo.image,
        },
        {
          property: 'og:url',
          content: seo.url,
        },
        {
          property: 'og:type',
          content: article ? 'article' : 'website',
        },
        {
          property: 'og:site_name',
          content: defaultTitle,
        },
        {
          property: 'og:locale',
          content: 'ta_IN',
        },
        // Twitter
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:creator',
          content: twitterUsername,
        },
        {
          name: 'twitter:title',
          content: seo.title,
        },
        {
          name: 'twitter:description',
          content: seo.description,
        },
        {
          name: 'twitter:image',
          content: seo.image,
        },
        {
          name: 'twitter:site',
          content: twitterUsername,
        },
        // Facebook
        {
          name: 'facebook:app_id',
          content: facebookAppId,
        },
        // Mobile
        {
          name: 'viewport',
          content: 'width=device-width, initial-scale=1, shrink-to-fit=no',
        },
        {
          name: 'theme-color',
          content: '#FF4B2B',
        },
        {
          name: 'msapplication-TileColor',
          content: '#FF4B2B',
        },
        {
          name: 'application-name',
          content: 'Thamizh Songs',
        },
        {
          name: 'apple-mobile-web-app-capable',
          content: 'yes',
        },
        {
          name: 'apple-mobile-web-app-status-bar-style',
          content: 'default',
        },
        {
          name: 'apple-mobile-web-app-title',
          content: 'Thamizh Songs',
        },
        {
          name: 'format-detection',
          content: 'telephone=no',
        },
        {
          name: 'mobile-web-app-capable',
          content: 'yes',
        },
        {
          name: 'msapplication-config',
          content: 'none',
        },
        {
          name: 'msapplication-tap-highlight',
          content: 'no',
        },
        // Additional SEO tags
        {
          name: 'robots',
          content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
        },
        {
          name: 'googlebot',
          content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        },
        {
          name: 'bingbot',
          content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        },
        // If article, add published and modified dates
        ...(article ? [
          {
            property: 'article:published_time',
            content: seo.publishedAt,
          },
          {
            property: 'article:modified_time',
            content: seo.modifiedAt,
          }
        ] : []),
      ]}
      link={[
        {
          rel: 'canonical',
          href: seo.url,
        },
        {
          rel: 'alternate',
          href: seo.url,
          hreflang: 'ta',
        },
        {
          rel: 'alternate',
          href: seo.url,
          hreflang: 'x-default',
        }
      ]}
    >
      {/* Add schema.org JSON-LD for rich results */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
      {children}
    </Helmet>
  )
}

SEO.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  image: PropTypes.string,
  article: PropTypes.bool,
  song: PropTypes.bool,
  album: PropTypes.bool,
  artist: PropTypes.bool,
  pathname: PropTypes.string,
  schema: PropTypes.object,
  keywords: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  publishedAt: PropTypes.string,
  modifiedAt: PropTypes.string,
  songData: PropTypes.object,
  albumData: PropTypes.object,
  artistData: PropTypes.object,
  children: PropTypes.node,
}

export default SEO
 